import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import * as d3 from "d3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";

const AttendanceSingleChart = () => {
    const [names, setNames] = useState([]);
    const [selectedName, setSelectedName] = useState(null);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [attendanceData, setAttendanceData] = useState([]);
    const chartRef = React.useRef();
    const [selectedCategory, setSelectedCategory] = useState({ value: "Single", label: "Single" });

    const categoryOptions = [
        { value: "All", label: "All" },
        { value: "Single", label: "Single" },
        { value: "Membership", label: "Membership" },
    ];

    const tooltipRef = useRef();

    useEffect(() => {
        fetchNames(selectedCategory.value);
        setSelectedName(null);
      }, [selectedCategory]);

      const fetchNames = async (category) => {
        try {
            let response;
    
            if (category === "Membership") {
                response = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getMembershipData`);
                const membershipData = response.data || {};
    
                const namesSet = new Set();
                Object.values(membershipData).forEach((membership) => {
                    membership.names.forEach((name) => namesSet.add(name));
                });
    
                const personsResponse = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getPersons`);
                const persons = personsResponse.data || {};
    
                const filteredNames = Object.values(persons)
                    .filter((person) => namesSet.has(person.name))
                    .map((person) => ({
                        id: person.personId,
                        label: `${person.personId}-${person.name}`,
                        value: person.name,
                    }));
                setNames(filteredNames);
            } else { 
                response = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getPersons`);
                const persons = response.data || {};
    
                const membershipResponse = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getMembershipData`);
                const membershipData = membershipResponse.data || {};
                const membershipNamesSet = new Set();
                Object.values(membershipData).forEach((membership) => {
                    membership.names.forEach((name) => membershipNamesSet.add(name));
                });
    
                const singleNamesArray = Object.values(persons)
                    .filter((person) => !membershipNamesSet.has(person.name)) // Exclude Membership names
                    .map((person) => ({
                        id: person.personId,
                        label: `${person.personId}-${person.name}`,
                        value: person.name,
                    }));
    
                setNames(singleNamesArray);
            }
        } catch (error) {
            console.error("Error fetching names:", error);
            setNames([]);
        }
    };
    
    useEffect(() => {
        fetchAttendance();
    }, [fromDate, toDate, selectedName]);

    const fetchAttendance = async () => {
        try {
            const response = await axios.get("https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getAttendance");
            setAttendanceData(response.data || []);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            setAttendanceData([]);
        }
    };

    const filteredData = attendanceData.filter((entry) => {
        const entryDate = new Date(entry.date.split("/").reverse().join("/"));
        return entryDate >= fromDate && entryDate <= toDate && (!selectedName || entry.personId.toString() === selectedName.id.toString());
    });

    const columns = [
        { field: "id", headerName: "S.No", width: 90 },
        { field: "date", headerName: "Date", width: 200 },
        { field: "status", headerName: "Status", width: 150 },
    ];

    const rows = filteredData.map((entry, index) => ({
        id: index + 1,
        date: entry.date,
        status: entry.status,
    }));

    useEffect(() => {
        if (!chartRef.current) return;
    
        const svg = d3.select(chartRef.current);
        svg.selectAll("*").remove();
    
        if (filteredData.length === 0) return;
    
        const margin = { top: 20, right: 30, bottom: 40, left: 50 };
        const width = 600 - margin.left - margin.right;
        const height = 400 - margin.top - margin.bottom;
    
        const x = d3
            .scaleBand()
            .domain(filteredData.map((d) => d.date))
            .range([0, width])
            .padding(0.2);
    
        const y = d3
            .scaleLinear()
            .domain([0, 1])
            .nice()
            .range([height, 0]);
    
        const color = d3.scaleOrdinal().domain(["Present", "Leave","Half Day"]).range(["#008000", "#f44336", "#808080"]);
    
        const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
        g.append("g").attr("transform", `translate(0,${height})`).call(d3.axisBottom(x));
        g.append("g").call(d3.axisLeft(y));
    
        const bars = g
            .selectAll("g.bar-group")
            .data(filteredData)
            .enter()
            .append("g")
            .attr("transform", (d) => `translate(${x(d.date)},0)`);
    
        bars.selectAll("rect")
            .data((d) => [
                { status: "Present", value: d.status === "Present" ? 1 : 0, date: d.date },
                { status: "Leave", value: d.status === "Leave" ? 1 : 0,  date: d.date },
                { status: "Half Day", value: d.status === "Half Day" ? 0.5 : 0, date: d.date },
            ])
            .enter()
            .append("rect")
            .attr("x", (d, i) => (x.bandwidth() / 3) * i) 
            .attr("y", y(0)) // Start from 0 for animation
            .attr("width", x.bandwidth() / 3)
            .attr("height", 0) // Start height from 0
            .attr("fill", (d) => color(d.status));
    
        // Tooltip logic
        bars.selectAll("rect")
        .on("mouseover", function (event, d) {
            d3.select(this).attr("opacity", 0.7); // Change opacity on hover

            // Set the tooltip content and position
            tooltipRef.current.style.visibility = "visible";
            tooltipRef.current.innerHTML = `
            <strong>Date:</strong> ${d.date}<br/>
            <strong>Status:</strong> ${d.status}<br/>
            <strong>Count:</strong> ${d.value}
        `;

            // Position the tooltip near the mouse cursor
            tooltipRef.current.style.left = `${event.pageX + 10}px`;
            tooltipRef.current.style.top = `${event.pageY - 30}px`;
        })
        .on("mouseout", function () {
            d3.select(this).attr("opacity", 1);
            tooltipRef.current.style.visibility = "hidden"; 
        })
        .transition()
        .duration(2000)
        .attr("y", (d) => y(d.value))
        .attr("height", (d) => height - y(d.value)); 
    });
    
    
    

    return (
        <div className="Attendanceperson" style={{ marginTop: "80px" }}>
            <h2 className="text-center mb-4">Person Attendance</h2>
            <div className="dateattendance" style={{ display: "flex", marginBottom: "20px" }}>
                <div>
                    <label>From Date:</label>
                    <DatePicker selected={fromDate} onChange={setFromDate} />
                </div>
                <div>
                    <label>To Date:</label>
                    <DatePicker selected={toDate} onChange={setToDate} />
                </div>
                <div>
                    <Select 
                        options={categoryOptions} 
                        value={selectedCategory} 
                        onChange={setSelectedCategory} 
                        placeholder="Select Category" 
                    />
                </div>
               
            </div>
            <div  className="dateattendance" style={{ display: "flex", marginBottom: "20px" }}>
            
                <Select options={names} value={selectedName} onChange={setSelectedName} placeholder="Select or search a name"  isDisabled={selectedCategory.value === "All"}/>
            </div>

           
            <h2 className="text-center mb-4">Daily Analysis</h2> 
            <div className="chart-legend" style={{ display: "flex", alignItems: "end", marginTop: "20px", right: "0" }}>
                <div style={{ display: "flex", alignItems: "center", marginRight: "15px" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#008000",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Present</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#f44336",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Leave</span>
                </div>
            </div> 
            <div
                ref={tooltipRef}
                style={{
                    position: "absolute",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    padding: "8px",
                    borderRadius: "4px",
                    visibility: "hidden",
                    pointerEvents: "none",
                    zIndex: 10,
                }}
            ></div>
            <svg ref={chartRef} className="Attendancechartref" width="600" height="400"></svg>

           <Box sx={{ height: 400, width: "100%", mt: 2 }}>
                {rows.length === 0 ? (
                    <Typography className="text-center">No attendance data available for the selected Person.</Typography>
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5, 10, 20]}
                        disableSelectionOnClick
                        style={{ }}
                        className="datagridsingleattendance"
                    />
                )}
            </Box>
        </div>
    );
};

export default AttendanceSingleChart;
